import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import emailjs from '@emailjs/browser';
import {
	BsArrowRight,
	BsCheck2,
	BsCheckLg,
	BsClipboard,
	BsEnvelope,
	BsFileEarmarkText,
	BsShieldCheck,
	BsTelephone,
} from 'react-icons/bs';
import { FaGithub, FaLinkedinIn } from 'react-icons/fa';
import { AppWrap } from '../../wrapper';
import './Contact.scss';

const EMAIL = 'rafaymalick68@gmail.com';
const PHONE = '+92 319 1945099';

const SERVICE_ID = 'service_llgbwvi';
const TEMPLATE_ID = 'template_mgdnmfj';
const PUBLIC_KEY = 'C8P6s-Xva5GjV6gbg';

const fadeUp = {
	hidden: { y: 40, opacity: 0 },
	visible: { y: 0, opacity: 1, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};

const projectTypes = ['WordPress','Shopify', 'Web app', 'Redesign', 'Something else'];

const validators = {
	name: (v) => {
		if (!v.trim()) return 'Please tell me your name.';
		if (v.trim().length < 2) return 'That name looks a little short.';
		return '';
	},
	email: (v) => {
		if (!v.trim()) return "I'll need an email address to reply.";
		if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim())) return "That email doesn't look right — check for typos.";
		return '';
	},
	message: (v) => {
		if (!v.trim()) return 'Tell me a little about your project.';
		if (v.trim().length < 10) return 'A few more words would help — what are you building?';
		return '';
	},
};

const MESSAGE_LIMIT = 1000;

const Contact = () => {
	const [formData, setFormData] = useState({ name: '', email: '', message: '' });
	const [subject, setSubject] = useState('');
	const [errors, setErrors] = useState({});
	const [status, setStatus] = useState('idle'); // idle | loading | success | error
	const [copied, setCopied] = useState(null);
	// kept separately so the success message can still greet by name after the form clears
	const [senderName, setSenderName] = useState('');

	const form = useRef();
	const fieldRefs = { name: useRef(), email: useRef(), message: useRef() };
	const copyTimer = useRef();

	const { name, email, message } = formData;

	useEffect(() => () => clearTimeout(copyTimer.current), []);

	const handleChange = (e) => {
		const { name: field, value } = e.target;
		setFormData((prev) => ({ ...prev, [field]: value }));
		// clear the error as soon as the user starts fixing it
		if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
	};

	// validate on blur, not on every keystroke
	const handleBlur = (e) => {
		const { name: field, value } = e.target;
		setErrors((prev) => ({ ...prev, [field]: validators[field](value) }));
	};

	const handleCopy = (value, key) => {
		if (!navigator.clipboard) return;
		navigator.clipboard.writeText(value).then(() => {
			setCopied(key);
			clearTimeout(copyTimer.current);
			copyTimer.current = setTimeout(() => setCopied(null), 1800);
		});
	};

	const handleSubmit = (e) => {
		e.preventDefault();

		const nextErrors = {
			name: validators.name(name),
			email: validators.email(email),
			message: validators.message(message),
		};
		setErrors(nextErrors);

		const firstInvalid = Object.keys(nextErrors).find((key) => nextErrors[key]);
		if (firstInvalid) {
			fieldRefs[firstInvalid].current?.focus();
			return;
		}

		// only bots fill the hidden field — show them success so they don't retry
		if (new FormData(form.current).get('_gotcha')) {
			setStatus('success');
			return;
		}

		setStatus('loading');

		const projectType = subject || 'Not specified';

		emailjs
			.send(
				SERVICE_ID,
				TEMPLATE_ID,
				{
					name: name.trim(),
					email: email.trim(),
					project_type: projectType,
					message: message.trim(),
					// lets "Reply" in the inbox go straight back to the sender
					reply_to: email.trim(),
				},
				PUBLIC_KEY
			)
			.then(
				() => {
					setSenderName(name.trim().split(' ')[0]);
					setStatus('success');
					setFormData({ name: '', email: '', message: '' });
					setSubject('');
				},
				() => setStatus('error')
			);
	};

	const resetForm = () => {
		setStatus('idle');
		setErrors({});
	};

	const loading = status === 'loading';

	const fieldProps = (field) => ({
		id: `contact-${field}`,
		name: field,
		ref: fieldRefs[field],
		value: formData[field],
		onChange: handleChange,
		onBlur: handleBlur,
		placeholder: ' ',
		disabled: loading,
		'aria-invalid': Boolean(errors[field]),
		'aria-describedby': errors[field] ? `contact-${field}-error` : undefined,
	});

	return (
		<div className='contact-content'>
			<motion.div
				className='contact-head'
				variants={fadeUp}
				initial='hidden'
				whileInView='visible'
				viewport={{ once: true, amount: 0.4 }}
			>
				<p className='eyebrow'>Get in touch</p>
				<h2 className='contact-heading'>
					Let's build <span className='text-gradient'>something great</span>
				</h2>
			</motion.div>

			<div className='contact-grid'>
				{/* ── left: channels + trust signals ─────────────────── */}
				<motion.aside
					className='contact-aside'
					variants={fadeUp}
					initial='hidden'
					whileInView='visible'
					viewport={{ once: true, amount: 0.2 }}
				>
					<p className='contact-availability'>
						<span className='availability-dot' aria-hidden='true' />
						Available for new projects
					</p>

					<p className='contact-intro'>
						Have a project in mind, or just want to compare notes? Drop me a line and
						I'll get back to you personally — usually within a day.
					</p>

					<ul className='contact-channels'>
						<li className='channel'>
							<span className='channel-icon' aria-hidden='true'>
								<BsEnvelope />
							</span>
							<span className='channel-body'>
								<span className='channel-label'>Email</span>
								<a className='channel-value' href={`mailto:${EMAIL}`}>
									{EMAIL}
								</a>
							</span>
							<button
								type='button'
								className='channel-copy'
								onClick={() => handleCopy(EMAIL, 'email')}
								aria-label={`Copy email address ${EMAIL}`}
							>
								{copied === 'email' ? <BsCheck2 /> : <BsClipboard />}
							</button>
						</li>

						<li className='channel'>
							<span className='channel-icon' aria-hidden='true'>
								<BsTelephone />
							</span>
							<span className='channel-body'>
								<span className='channel-label'>Phone</span>
								<a className='channel-value' href={`tel:${PHONE.replace(/\s/g, '')}`}>
									{PHONE}
								</a>
							</span>
							<button
								type='button'
								className='channel-copy'
								onClick={() => handleCopy(PHONE, 'phone')}
								aria-label={`Copy phone number ${PHONE}`}
							>
								{copied === 'phone' ? <BsCheck2 /> : <BsClipboard />}
							</button>
						</li>
					</ul>

					<span className='contact-copied' role='status' aria-live='polite'>
						{copied ? 'Copied to clipboard' : ''}
					</span>

					<div className='contact-facts'>
						<div className='fact'>
							<span className='fact-value'>&lt; 24h</span>
							<span className='fact-label'>Average reply</span>
						</div>
						<div className='fact'>
							<span className='fact-value'>Free</span>
							<span className='fact-label'>First consultation</span>
						</div>
					</div>

					<div className='contact-extras'>
						<a
							href='/Updated_resume_rafay.pdf'
							target='_blank'
							rel='noreferrer'
							className='btn btn-ghost contact-resume'
						>
							<BsFileEarmarkText aria-hidden='true' /> View resume
						</a>

						<div className='contact-socials'>
							<a
								href='https://github.com/Abdul-Rafay-2002'
								target='_blank'
								rel='noreferrer'
								aria-label='GitHub'
							>
								<FaGithub />
							</a>
							<a
								href='https://www.linkedin.com/in/abdul-rafay-3bb521231/'
								target='_blank'
								rel='noreferrer'
								aria-label='LinkedIn'
							>
								<FaLinkedinIn />
							</a>
						</div>
					</div>
				</motion.aside>

				{/* ── right: acrylic form card ─────────────────── */}
				<motion.div
					className='contact-card'
					variants={fadeUp}
					initial='hidden'
					whileInView='visible'
					viewport={{ once: true, amount: 0.2 }}
				>
					<AnimatePresence mode='wait' initial={false}>
						{status === 'success' ? (
							<motion.div
								className='contact-success'
								key='success'
								initial={{ opacity: 0, y: 16 }}
								animate={{ opacity: 1, y: 0 }}
								exit={{ opacity: 0, y: -16 }}
								transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
							>
								<motion.span
									className='success-badge'
									initial={{ scale: 0.6, opacity: 0 }}
									animate={{ scale: 1, opacity: 1 }}
									transition={{ delay: 0.08, type: 'spring', stiffness: 260, damping: 18 }}
									aria-hidden='true'
								>
									<BsCheckLg />
								</motion.span>
								<h3 className='success-title'>Message sent</h3>
								<p className='success-text'>
									Thanks{senderName ? `, ${senderName}` : ''} — your message landed safely.
									I'll reply to you within 24 hours.
								</p>
								<button type='button' className='btn btn-ghost' onClick={resetForm}>
									Send another message
								</button>
							</motion.div>
						) : (
							<motion.form
								key='form'
								ref={form}
								onSubmit={handleSubmit}
								className='contact-form'
								noValidate
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
								transition={{ duration: 0.3 }}
							>
								<div className='contact-form-head'>
									<h3 className='contact-form-title'>Send a message</h3>
									<p className='contact-form-sub'>Three fields. That's it.</p>
								</div>

								<div className='field-row'>
									<div className={`field ${errors.name ? 'has-error' : ''}`}>
										<input type='text' autoComplete='name' {...fieldProps('name')} />
										<label htmlFor='contact-name'>Your name</label>
										{errors.name && (
											<p className='field-error' id='contact-name-error' role='alert'>
												{errors.name}
											</p>
										)}
									</div>

									<div className={`field ${errors.email ? 'has-error' : ''}`}>
										<input type='email' autoComplete='email' {...fieldProps('email')} />
										<label htmlFor='contact-email'>Your email</label>
										{errors.email && (
											<p className='field-error' id='contact-email-error' role='alert'>
												{errors.email}
											</p>
										)}
									</div>
								</div>

								<fieldset className='field-chips'>
									<legend>What's this about? <span>Optional</span></legend>
									<div className='chip-row'>
										{projectTypes.map((type) => (
											<button
												type='button'
												key={type}
												className={`chip ${subject === type ? 'active' : ''}`}
												onClick={() => setSubject(subject === type ? '' : type)}
												aria-pressed={subject === type}
												disabled={loading}
											>
												{type}
											</button>
										))}
									</div>
								</fieldset>

								<div className={`field field-message ${errors.message ? 'has-error' : ''}`}>
									<textarea rows='6' maxLength={MESSAGE_LIMIT} {...fieldProps('message')} />
									<label htmlFor='contact-message'>Your message</label>
									<span className='field-count' aria-hidden='true'>
										{message.length}/{MESSAGE_LIMIT}
									</span>
									{errors.message && (
										<p className='field-error' id='contact-message-error' role='alert'>
											{errors.message}
										</p>
									)}
								</div>

								{/* honeypot — hidden from humans and assistive tech */}
								<input
									type='text'
									name='_gotcha'
									className='contact-honeypot'
									tabIndex={-1}
									autoComplete='off'
									aria-hidden='true'
								/>

								<button
									type='submit'
									className='btn btn-gradient contact-submit'
									disabled={loading}
								>
									{loading ? (
										<>
											<span className='spinner' aria-hidden='true' /> Sending…
										</>
									) : (
										<>
											Send message <BsArrowRight aria-hidden='true' />
										</>
									)}
								</button>

								<p className='contact-microcopy'>
									<BsShieldCheck aria-hidden='true' /> Your details stay private. No spam,
									no lists — ever.
								</p>

								<div className='contact-status' role='status' aria-live='polite'>
									{status === 'error' && (
										<p className='status-error'>
											Something went wrong sending that. Try again, or email me directly at{' '}
											<a href={`mailto:${EMAIL}`}>{EMAIL}</a>.
										</p>
									)}
								</div>
							</motion.form>
						)}
					</AnimatePresence>
				</motion.div>
			</div>
		</div>
	);
};

export default AppWrap(Contact, 'contact');
